import { getStoredPaymentStatus, PaymentStatusLabel } from '@bariendo/lib/payment-status';
import { Patient } from '@medplum/fhirtypes';
import { TreatmentPaymentOrder } from '@/components/Timeline/types';
import { getBariatricProcedureStageLabel } from '@/services/patient-fields';
import { LedgerRow } from './ledgerRows';

/** A patient the Connect popover can offer, with their open order when they have one. */
export interface ConnectCandidate {
  patient: Patient;
  order?: TreatmentPaymentOrder;
}

/** The prototype's dashed `.connect` button: Connect, and the Imports preview's `Use <name>`. */
export const CONNECT_BUTTON_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '1px 8px',
  borderRadius: 6,
  border: '1px dashed rgba(36, 55, 33, 0.45)',
  color: 'var(--ev-color-green-dark, #243721)',
  fontSize: 12,
  lineHeight: '18px',
  fontWeight: 500,
  whiteSpace: 'nowrap' as const,
};

export const SUGGESTION_COUNT = 3;
export const SEARCH_RESULT_COUNT = 6;

const cents = (amount: number): number => Math.round(amount * 100);

/** Name matches on the payer, those with an open balance of exactly this amount first. */
export function rankCandidates(candidates: ConnectCandidate[], amount: number): ConnectCandidate[] {
  const matchesBalance = (candidate: ConnectCandidate): boolean =>
    candidate.order !== undefined && cents(candidate.order.balance) === cents(Math.abs(amount));
  return [...candidates.filter(matchesBalance), ...candidates.filter((candidate) => !matchesBalance(candidate))];
}

/**
 * The option's second line, `Stage · Payment status · Procedure`, from the
 * patient's stage, the payment status the stage bot stored (BAR-3084) and
 * their open order's procedure.
 */
export function candidateLine({ patient, order }: ConnectCandidate): string {
  const stored = getStoredPaymentStatus(patient);
  const paymentStatus = stored ? PaymentStatusLabel[stored.status] : '';
  const procedure = order?.items.find((item) => item.serviceName)?.serviceName ?? '';
  return [getBariatricProcedureStageLabel(patient), paymentStatus, procedure].filter(Boolean).join(' · ');
}

/**
 * Whether Disconnect can undo the row's patient: a ledger link always, an
 * attach while its order isn't PAID (reopening a paid order is BAR-3049), and
 * never a refund that takes its patient from its payment.
 */
export function canDisconnect(row: LedgerRow): boolean {
  return (
    row.patientSource === 'link' ||
    (row.patientSource === 'order' && row.record === 'charge' && row.orderStatus !== 'PAID')
  );
}
